import * as React from 'react';
import { AlertDialog as AlertDialogPrimitive } from 'radix-ui';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import {
  alertDialogFooterDestructiveDividerClassName,
  alertDialogFooterVariantClasses,
  alertDialogHeaderCompactPaddingClassName,
  alertDialogHeaderVariantClasses,
  dialogFooterDividerClassName,
  dialogFooterTertiaryButtonClassName,
  dialogHeaderPaddingClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { isEventFromReactSelectMenu } from '#/modules/commonComponents/dialog/isEventFromReactSelectMenu';

function AlertDialog({
  ...props
}) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

function AlertDialogTrigger({
  ...props
}) {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

function AlertDialogPortal({
  ...props
}) {
  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}

function AlertDialogOverlay({
  className,
  ...props
}) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        // eslint-disable-next-line max-len
        'fixed inset-0 z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogContent({
  className,
  size = 'default',
  closeOnOutsideClick = true,
  closeOnEscape = true,
  onPointerDownOutside,
  onInteractOutside,
  onFocusOutside,
  onEscapeKeyDown,
  ...props
}) {
  function handlePointerDownOutside(event) {
    if (isEventFromReactSelectMenu(event)) {
      event.preventDefault();
      onPointerDownOutside?.(event);
      return;
    }
    if (!closeOnOutsideClick) {
      event.preventDefault();
    }
    onPointerDownOutside?.(event);
  }

  function handleInteractOutside(event) {
    if (isEventFromReactSelectMenu(event)) {
      event.preventDefault();
      onInteractOutside?.(event);
      return;
    }
    if (!closeOnOutsideClick) {
      event.preventDefault();
    }
    onInteractOutside?.(event);
  }

  function handleFocusOutside(event) {
    if (isEventFromReactSelectMenu(event)) {
      event.preventDefault();
      onFocusOutside?.(event);
      return;
    }
    onFocusOutside?.(event);
  }

  function handleEscapeKeyDown(event) {
    if (!closeOnEscape) {
      event.preventDefault();
    }
    onEscapeKeyDown?.(event);
  }

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        data-size={size}
        className={cn(
          // eslint-disable-next-line max-len
          'group/alert-dialog-content fixed top-1/2 left-1/2 z-50 grid w-full -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none data-[size=default]:max-w-xs data-[size=sm]:max-w-xs data-[size=default]:sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          className,
        )}
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        onFocusOutside={handleFocusOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
        {...props}
      />
    </AlertDialogPortal>
  );
}

function AlertDialogHeader({
  className,
  variant = 'muted',
  compact = false,
  ...props
}) {
  return (
    <div
      data-slot="alert-dialog-header"
      data-variant={variant}
      className={cn(
        alertDialogHeaderVariantClasses[variant],
        compact ? alertDialogHeaderCompactPaddingClassName : dialogHeaderPaddingClassName,
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogFooter({
  className,
  divider = 'top',
  dividerClassName,
  variant = 'muted',
  ...props
}) {
  const resolvedDividerClassName = dividerClassName ?? (
    divider === 'top'
      ? (variant === 'destructive'
        ? alertDialogFooterDestructiveDividerClassName
        : dialogFooterDividerClassName)
      : null
  );

  return (
    <div
      data-slot="alert-dialog-footer"
      data-variant={variant}
      className={cn(
        alertDialogFooterVariantClasses[variant],
        resolvedDividerClassName,
        dialogFooterTertiaryButtonClassName,
        'group-data-[size=sm]/alert-dialog-content:grid group-data-[size=sm]/alert-dialog-content:grid-cols-2',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogMedia({
  className,
  ...props
}) {
  return (
    <div
      data-slot="alert-dialog-media"
      className={cn(
        // eslint-disable-next-line max-len
        'mb-2 inline-flex size-10 items-center justify-center rounded-md bg-muted sm:group-data-[size=default]/alert-dialog-content:row-span-2 *:[svg:not([class*=\'size-\'])]:size-6',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogTitle({
  className,
  ...props
}) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(
        // eslint-disable-next-line max-len
        'text-lg font-semibold leading-tight sm:group-data-[size=default]/alert-dialog-content:group-has-data-[slot=alert-dialog-media]/alert-dialog-content:col-start-2',
        'group-data-[variant=muted]/alert-dialog-header:text-slate-900',
        'group-data-[variant=destructive]/alert-dialog-header:text-white',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogDescription({
  className,
  ...props
}) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn(
        // eslint-disable-next-line max-len
        'text-sm text-balance text-muted-foreground md:text-pretty *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        'group-data-[variant=muted]/alert-dialog-header:text-slate-600',
        'group-data-[variant=muted]/alert-dialog-header:*:[a]:hover:text-slate-900',
        'group-data-[variant=destructive]/alert-dialog-header:text-white/80',
        'group-data-[variant=destructive]/alert-dialog-header:*:[a]:hover:text-white',
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogAction({
  className,
  variant = 'default',
  size = 'default',
  ...props
}) {
  return (
    <Button variant={variant} size={size} asChild>
      <AlertDialogPrimitive.Action data-slot="alert-dialog-action" className={cn(className)} {...props} />
    </Button>
  );
}

function AlertDialogCancel({
  className,
  variant = 'outline',
  size = 'default',
  ...props
}) {
  return (
    <Button variant={variant} size={size} asChild>
      <AlertDialogPrimitive.Cancel data-slot="alert-dialog-cancel" className={cn(className)} {...props} />
    </Button>
  );
}

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
};
